import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import {
  recordCategorySnapshots,
  fetchDataReportsAdmin,
  fetchPageFeedbackAdmin,
  fetchForumClaimsAdmin,
  updateDataReportStatus,
  updateForumClaimStatus,
} from '../../services/featureDb';
import { useTheme } from '../../contexts/ThemeContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

import AdminCachePanel from './AdminCachePanel';

type Tab = 'feedback' | 'webhooks' | 'dead' | 'hof' | 'snapshots' | 'cache';
type FeedbackSub = 'reports' | 'helpful' | 'claims';

type DataReport = {
  id: string;
  message: string | null;
  status: string;
  report_type: string;
  created_at: string;
  forums: { name: string; forum_id: string } | null;
};

type PageFeedback = {
  id: string;
  helpful: boolean;
  visitor_hash: string;
  created_at: string;
  forums: { name: string; forum_id: string } | null;
};

type ForumClaim = {
  id: string;
  contact_email: string;
  status: string;
  created_at: string;
  forums: { name: string; forum_id: string } | null;
};

const AdminOpsHub: React.FC = () => {
  const { darkMode } = useTheme();
  const [tab, setTab] = useState<Tab>('feedback');
  const [feedbackSub, setFeedbackSub] = useState<FeedbackSub>('reports');
  const [reports, setReports] = useState<DataReport[]>([]);
  const [pageFeedback, setPageFeedback] = useState<PageFeedback[]>([]);
  const [claims, setClaims] = useState<ForumClaim[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [deadForums, setDeadForums] = useState<Array<{ id: string; name: string; updated_at: string }>>([]);
  const [hof, setHof] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChat, setTelegramChat] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      if (feedbackSub === 'reports') {
        const { data, error } = await fetchDataReportsAdmin();
        if (error) {
          setFeedbackError(error);
          setReports([]);
        } else {
          setReports(data as DataReport[]);
        }
      } else if (feedbackSub === 'helpful') {
        const { data, error } = await fetchPageFeedbackAdmin();
        if (error) {
          setFeedbackError(error);
          setPageFeedback([]);
        } else {
          setPageFeedback(data as PageFeedback[]);
        }
      } else {
        const { data, error } = await fetchForumClaimsAdmin();
        if (error) {
          setFeedbackError(error);
          setClaims([]);
        } else {
          setClaims(data as ForumClaim[]);
        }
      }
    } catch (err) {
      console.error(err);
      setFeedbackError('Geri bildirimler yüklenemedi');
    } finally {
      setFeedbackLoading(false);
    }
  }, [feedbackSub]);

  useEffect(() => {
    if (tab === 'feedback') loadFeedback();
  }, [tab, feedbackSub, loadFeedback]);

  useEffect(() => {
    if (tab === 'dead') {
      const cutoff = new Date(Date.now() - 90 * 86400000).toISOString();
      supabase
        .from('forums')
        .select('id, name, updated_at')
        .eq('is_active', true)
        .lt('updated_at', cutoff)
        .then(({ data }) => setDeadForums(data || []));
    }
    if (tab === 'hof') {
      supabase.from('hall_of_fame_entries').select('id, name, slug').then(({ data }) => setHof(data || []));
    }
    if (tab === 'webhooks') {
      supabase
        .from('site_settings')
        .select('webhook_url, telegram_bot_token, telegram_chat_id')
        .limit(1)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setWebhookUrl(data.webhook_url || '');
            setTelegramToken(data.telegram_bot_token || '');
            setTelegramChat(data.telegram_chat_id || '');
          }
        });
    }
  }, [tab]);

  const updateReportStatus = async (id: string, status: string) => {
    const { ok, error } = await updateDataReportStatus(id, status);
    if (!ok) toast.error(error || 'Güncellenemedi');
    else {
      toast.success('Durum güncellendi');
      loadFeedback();
    }
  };

  const updateClaimStatus = async (id: string, status: string) => {
    const { ok, error } = await updateForumClaimStatus(id, status);
    if (!ok) toast.error(error || 'Güncellenemedi');
    else {
      toast.success('Durum güncellendi');
      loadFeedback();
    }
  };

  const saveWebhooks = async () => {
    const { data: rows } = await supabase.from('site_settings').select('id').limit(1);
    const id = rows?.[0]?.id;
    if (!id) {
      toast.error('site_settings kaydı yok');
      return;
    }
    const { error } = await supabase
      .from('site_settings')
      .update({
        webhook_url: webhookUrl || null,
        telegram_bot_token: telegramToken || null,
        telegram_chat_id: telegramChat || null,
      })
      .eq('id', id);
    if (error) toast.error(error.message);
    else toast.success('Kaydedildi');
  };

  const runSnapshots = async () => {
    await recordCategorySnapshots();
    toast.success('Kategori snapshot kaydı denendi');
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'feedback', label: 'Geri bildirimler' },
    { id: 'webhooks', label: 'Webhook / Telegram' },
    { id: 'dead', label: 'Ölü site adayları' },
    { id: 'hof', label: 'Hall of Fame' },
    { id: 'snapshots', label: 'Snapshot' },
    { id: 'cache', label: 'Önbellek' },
  ];

  const feedbackTabs: { id: FeedbackSub; label: string }[] = [
    { id: 'reports', label: 'Veri hatalı' },
    { id: 'helpful', label: 'Faydalı mı?' },
    { id: 'claims', label: 'Sahiplik talepleri' },
  ];

  const box = darkMode ? 'bg-gray-800 text-gray-200 border-gray-700' : 'bg-white text-gray-800 border-gray-200';
  const subBtn = (active: boolean) =>
    active
      ? 'bg-indigo-600 text-white'
      : darkMode
        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200';

  const fmtDate = (iso: string) => format(new Date(iso), 'dd MMM yyyy HH:mm', { locale: tr });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Operasyon merkezi</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              tab === t.id ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={`rounded-xl border p-6 ${box}`}>
        {tab === 'feedback' && (
          <>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Forum detay sayfasından gelen ziyaretçi bildirimleri.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {feedbackTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFeedbackSub(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${subBtn(feedbackSub === t.id)}`}
                >
                  {t.label}
                </button>
              ))}
              <button
                type="button"
                onClick={loadFeedback}
                className={`ml-auto px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
              >
                Yenile
              </button>
            </div>

            {feedbackError && (
              <div
                className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                  darkMode
                    ? 'bg-red-950/40 border-red-800 text-red-200'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {feedbackError}
              </div>
            )}

            {feedbackLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
              </div>
            ) : feedbackSub === 'reports' ? (
              <ul className="space-y-3 text-sm">
                {reports.length === 0 && <li className="text-gray-500">Henüz veri hatası bildirimi yok.</li>}
                {reports.map((r) => (
                  <li
                    key={r.id}
                    className={`rounded-lg border p-3 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <Link
                          to={`/forum/${r.forums?.forum_id || ''}`}
                          className="font-semibold text-indigo-500 hover:underline"
                          target="_blank"
                        >
                          {r.forums?.name || 'Forum'}
                        </Link>
                        <p className="mt-1">{r.message || '—'}</p>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {fmtDate(r.created_at)} · {r.report_type} · {r.status}
                        </p>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateReportStatus(r.id, 'resolved')}
                            className="px-2 py-1 text-xs rounded bg-green-600 text-white"
                          >
                            Çözüldü
                          </button>
                          <button
                            type="button"
                            onClick={() => updateReportStatus(r.id, 'dismissed')}
                            className="px-2 py-1 text-xs rounded bg-gray-500 text-white"
                          >
                            Reddet
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : feedbackSub === 'helpful' ? (
              <ul className="space-y-2 text-sm">
                {pageFeedback.length === 0 && <li className="text-gray-500">Henüz oylama yok.</li>}
                {pageFeedback.map((f) => (
                  <li
                    key={f.id}
                    className={`rounded-lg border px-3 py-2 flex flex-wrap justify-between gap-2 ${
                      darkMode ? 'border-gray-600' : 'border-gray-200'
                    }`}
                  >
                    <span>
                      <Link
                        to={`/forum/${f.forums?.forum_id || ''}`}
                        className="font-medium text-indigo-500 hover:underline"
                        target="_blank"
                      >
                        {f.forums?.name || 'Forum'}
                      </Link>
                      {' — '}
                      <span className={f.helpful ? 'text-green-500' : 'text-red-400'}>
                        {f.helpful ? 'Faydalı' : 'Faydalı değil'}
                      </span>
                    </span>
                    <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>{fmtDate(f.created_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-3 text-sm">
                {claims.length === 0 && <li className="text-gray-500">Henüz sahiplik talebi yok.</li>}
                {claims.map((c) => (
                  <li
                    key={c.id}
                    className={`rounded-lg border p-3 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <Link
                          to={`/forum/${c.forums?.forum_id || ''}`}
                          className="font-semibold text-indigo-500 hover:underline"
                          target="_blank"
                        >
                          {c.forums?.name || 'Forum'}
                        </Link>
                        <p className="mt-1">
                          <a href={`mailto:${c.contact_email}`} className="text-indigo-400 hover:underline">
                            {c.contact_email}
                          </a>
                        </p>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {fmtDate(c.created_at)} · {c.status}
                        </p>
                      </div>
                      {c.status === 'pending' && (
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateClaimStatus(c.id, 'approved')}
                            className="px-2 py-1 text-xs rounded bg-green-600 text-white"
                          >
                            Onayla
                          </button>
                          <button
                            type="button"
                            onClick={() => updateClaimStatus(c.id, 'rejected')}
                            className="px-2 py-1 text-xs rounded bg-gray-500 text-white"
                          >
                            Reddet
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {tab === 'webhooks' && (
          <form
            className="grid gap-3 max-w-lg"
            onSubmit={(e) => {
              e.preventDefault();
              void saveWebhooks();
            }}
          >
            <input
              className={`border rounded px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
              placeholder="Webhook URL"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <input
              className={`border rounded px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
              placeholder="Telegram bot token"
              value={telegramToken}
              onChange={(e) => setTelegramToken(e.target.value)}
            />
            <input
              className={`border rounded px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
              placeholder="Telegram chat id"
              value={telegramChat}
              onChange={(e) => setTelegramChat(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded-lg w-fit">
                Kaydet
              </button>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enter ile kaydet</span>
            </div>
          </form>
        )}

        {tab === 'dead' && (
          <ul className="text-sm space-y-1">
            {deadForums.map((f) => (
              <li key={f.id}>
                {f.name} — son güncelleme: {f.updated_at ? new Date(f.updated_at).toLocaleDateString('tr-TR') : '—'}
              </li>
            ))}
            {!deadForums.length && <li>90 gündür güncellenmeyen aktif forum yok.</li>}
          </ul>
        )}

        {tab === 'hof' && (
          <p className="text-sm">{hof.length} Hall of Fame kaydı listeleniyor.</p>
        )}

        {tab === 'snapshots' && (
          <div>
            <p className="text-sm mb-4">Kategori toplamlarını snapshot tablosuna yazar (grafikler için).</p>
            <button type="button" onClick={runSnapshots} className="bg-indigo-600 text-white px-4 py-2 rounded-lg">
              Snapshot al
            </button>
            <a href="/admin/report" target="_blank" rel="noreferrer" className="block mt-4 text-indigo-500 text-sm">
              Aylık rapor (yazdır / PDF)
            </a>
          </div>
        )}

        {tab === 'cache' && <AdminCachePanel />}
      </div>
    </div>
  );
};

export default AdminOpsHub;
